import $ from 'jquery';
import debounce from 'just-debounce';
import rimg from '@pixelunion/rimg-shopify';
import SearchForm from './SearchForm';
import * as Dimmer from '../../helpers/Dimmer';

export default class LiveSearch {
  constructor(els, settings) {
    this.$el = $(els.el);
    this.$body = $('body');
    this.$header = $(els.header);
    this.$document = $(document);

    this.settings = settings;
    this.formOptions = { liveSearch: true };
    this.contentTypes = this.settings.content_types;
    this.enableImages = true;
    this.flyDownActive = false;
    this.takeOverActive = false;
    this.showFlyDown = false;

    this.searchOpenClass = 'site-header-search--open';

    this.$form = this.$el.find('[data-live-search-form]');
    this.$input = this.$form.find('[data-live-search-input]');
    this.$button = this.$form.find('[data-live-search-submit]');
    this.$takeOverCancel = this.$form.find('[data-live-search-takeover-cancel]');

    this.$flyDown = this.$el.find('[data-live-search-flydown]');
    this.$searchResults = this.$flyDown.find('[data-live-search-results]');
    this.$searchPlaceholder = this.$flyDown.find('[data-live-search-placeholder]');
    this.$quickLinks = this.$flyDown.find('[data-live-search-quick-links]');

    this.staticSearch = new SearchForm(this.$el, this.formOptions);
    this._search = this._search.bind(this);
    this._toggleTakeOver = this._toggleTakeOver.bind(this);
    this._searchFocused = this._searchFocused.bind(this);
    this._documentFocus = this._documentFocus.bind(this);

    this._closeEsc = e => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        this.$input.focus();
        this.$input.val('');
        this._closeFlyDown(true);
      }
    };

    this.events = [
      this.$button.on('click.live-search', event => {
        event.preventDefault();
        this.$form.submit();
      }),
      this.$input.on('keyup.live-search', debounce(this._search, 250)),
      this.$input.on('focus.live-search', this._searchFocused),
      this.$takeOverCancel.on('click.live-search', this._toggleTakeOver),
    ];
  }

  unload() {
    this.events.forEach($el => $el.off('.live-search'));

    this.staticSearch.unload();
  }

  /**
   * If is searching, only show placeholder
   *
   * @private
   */
  _flyDownSearching() {
    this.$searchResults.removeClass('visible');
    this.$quickLinks.removeClass('visible');
    this.$flyDown[0].style.setProperty('--open-height', 'auto');

    this.$searchPlaceholder.addClass('visible');
  }

  /**
   * Search field has been focused, or has been cleared of contents
   *   - If previous search contents, show
   *   - If no previous search contents, show quicklinks if exists
   *
   * @param hasTerms
   * @private
   */
  _flyDownInitial(hasTerms) {
    this.showFlyDown = false;

    const hasNoResults = this.$searchResults.find('[data-live-search-no-products]').length === 0;
    const hasPreviousResults = this.$searchResults.children().length > 0;

    if (hasTerms && hasNoResults && hasPreviousResults) {
      this.$searchResults.addClass('visible');
      this.$quickLinks.removeClass('visible');
      this.showFlyDown = true;
    } else if (this.$quickLinks.length) {
      this.$searchResults.removeClass('visible');
      this.$quickLinks.addClass('visible');
      this.showFlyDown = true;
    }
  }

  _search(event) {
    // Ignore non character key strokes
    const invalidKeyStrokes = [
      'Alt',
      'ArrowRight',
      'ArrowLeft',
      'ArrowUp',
      'ArrowDown',
      'Capslock',
      'Control',
      'Escape',
      'Meta',
      'Shift',
      'Tab',
      'Enter',
    ];

    if (event.key && invalidKeyStrokes.indexOf(event.key) !== -1) {
      return;
    }

    const terms = this.$input.val();
    const hasTerms = terms.length > 0;

    if (!hasTerms) {
      if (!this.takeOverActive) {
        this._flyDownInitial(false);
        this._closeFlyDown(true);
      } else {
        // Clear previous results
        this.$searchResults.html('');
      }

      return;
    }

    this._flyDownSearching();

    // Clear previous results
    this.$searchResults.html('');

    // If terms has length, this will be true
    this._toggleButton(hasTerms);

    // Show the flyout if it isn't open
    if (!this.$flyDown.data('is-open')) {
      this.showFlyDown = true;
      this._openFlyDown();
    }

    fetch(`${window.Theme.routes.predictive_search_url}?q=${encodeURIComponent(terms)}&section_id=predictive-search`)
      .then(response => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then(html => {
        this.$searchResults.html(html);
        this.$searchPlaceholder.removeClass('visible');
        rimg.watch(this.$searchResults[0]);
        this.$searchResults.addClass('visible');
        this._setOpenHeight();
        this._openFlyDown();
        this._toggleButton(false);
      })
      .catch(error => {
        throw error;
      });
  }

  _searchError(response) {
    console.warn('Search had error');
    console.log(response.message, response.error, response.event);
    this._toggleButton(false);
  }

  /**
   * Search has been cancelled due to new search being performed
   *
   * @private
   */
  _searchCancelled() {
    // Searches are canceled on user interaction, UI should remain unchanged
  }

  /**
   * Toggles search button while processing
   *
   * @param disable
   * @private
   */
  _toggleButton(disable) {
    if (disable) {
      this.$button
        .addClass('search-icon--processing')
        .attr('disabled');
    } else {
      this.$button
        .removeClass('search-icon--processing')
        .removeAttr('disabled');
    }
  }

  _searchFocused(e) {
    e.stopPropagation();
    if (this.flyDownActive) {
      return;
    }

    const hasTerms = this.$input.val().length > 0;

    this.$el.addClass('live-search--focused');

    this._flyDownInitial(hasTerms);

    this._openFlyDown();
  }

  _setOpenHeight() {
    const flyDownEl = this.$flyDown[0];
    const container = flyDownEl.querySelector('[data-predictive-search]');

    if (container) {
      const { scrollHeight } = container;
      flyDownEl.style.setProperty('--open-height', `${scrollHeight}px`);
    }
  }

  _openFlyDown() {
    if (!this.showFlyDown) {
      return;
    }

    window.addEventListener('keydown', this._closeEsc);

    this.$el.addClass('live-search--active');
    this.$flyDown.data('is-open', true);

    this._setOpenHeight();

    if (this.$el.closest('[data-site-header]').length) {
      this.$body[0].classList.add('scroll-lock');
      Dimmer.dim(this);
    }

    // if this is the dynamic search section, don't use the overlay
    this.flyDownActive = true;

    // Only listen for events outside of the search flydown when its open
    this.closeEvents = [
      this.$document.on('focusin.live-search', this._documentFocus),
      this.$document.on('touchstart.live-search', this._documentFocus),
      this.$document.on('click.live-search', this._documentFocus),
    ];
  }

  /**
   * Close the FlyDown when no longer needed
   *  - Keep focus styling if input is still being interacted with
   *
   * @param retainFocus
   * @private
   */
  _closeFlyDown(retainFocus = false) {
    if (!this.flyDownActive) {
      if (!retainFocus) {
        this.$el.removeClass('live-search--focused');
      }
      return;
    }

    if (this.takeOverActive) {
      this._toggleTakeOver(null, false);
    }

    this.$flyDown.data('is-open', false);
    this.$el.removeClass('live-search--active');

    if (!retainFocus) {
      this.$el.removeClass('live-search--focused');
    }

    this.flyDownActive = false;
    if (this.closeEvents && this.closeEvents.length) {
      this.closeEvents.forEach($el => {
        $el.off('.live-search');
      });
    }

    window.removeEventListener('keydown', this._closeEsc);

    if (this.$el.closest('[data-site-header]').length) {
      Dimmer.clear(this);
      this.$body[0].classList.remove('scroll-lock');
    }

    this.closeEvents = [];
  }

  /**
   * Open or close the Take over for mobile
   *
   *
   * @param event
   * @param enable
   * @private
   */
  _toggleTakeOver(event = null, enable = false) {
    this.takeOverActive = enable;

    if (event) {
      event.preventDefault();
    }

    if (!enable) {
      this._closeFlyDown(false);
    }

    if (this.$header.hasClass('search--section')) {
      this.$body.toggleClass('search-takeover-active', enable);
    }

    this.$el.toggleClass('live-search--takeover', enable);
    this.$body.toggleClass('scroll-lock', enable);
  }

  openMobileTakeover() {
    this._toggleTakeOver(null, true);

    window.addEventListener('keydown', this._closeEsc);

    this.$input.focus();
  }

  /**
   * When the focus element has changed, either by clicking, touching, or tabbing
   * check to see if it is within the flydown
   *
   * @param event
   * @private
   */
  _documentFocus(event) {
    const $parent = $(event.target).parents('[data-live-search]');

    if ($parent.length) {
      return;
    }

    this._closeFlyDown();
  }
}
